import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  hospitalityFrontDeskTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  HospitalityFrontDeskBoard,
  HospitalityFrontDeskRoom,
  HospitalityProperty
} from './catalog.service';

const ROOM_STATUSES = [
  'Available', 'Reserved', 'Occupied', 'Dirty', 'Cleaning', 'Inspected', 'OutOfService', 'Maintenance', 'Blocked'
] as const;

@Component({
  standalone: true,
  selector: 'ain-hospitality-front-desk',
  imports: [
    DatePipe, DecimalPipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    ButtonModule, SelectModule, DialogModule, CheckboxModule, TextareaModule
  ],
  template: `
    <ain-page [title]="t('catalog.hospitality.frontDesk.title')" [description]="t('catalog.hospitality.frontDesk.description')">
      @if (loading() && !board()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <p-select
              [options]="propertyOptions()"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="onPropertyChange()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.hospitality.property')"
              [filter]="true"
              appendTo="body" />
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="loadBoard()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
            @if (board()) {
              <span class="updated-at">{{ t('catalog.hospitality.frontDesk.updatedAt') }}: {{ board()!.generatedAtUtc | date:'mediumTime' }}</span>
            }
            <a routerLink="/hospitality/reservations">{{ t('catalog.hospitality.reservations.title') }}</a>
            <a routerLink="/hospitality/booking-board">{{ t('catalog.hospitality.bookingBoard.title') }}</a>
          </div>

          @if (!selectedPropertyId) {
            <ain-state type="empty" [title]="t('catalog.hospitality.frontDesk.selectProperty')" />
          } @else if ((board()?.rooms?.length ?? 0) === 0) {
            <ain-state type="empty" [title]="t('catalog.hospitality.frontDesk.noRooms')" />
          } @else {
            <div class="summary-row">
              <button
                type="button"
                class="summary-chip"
                [class.active]="statusFilter() === ''"
                (click)="setStatusFilter('')">
                {{ t('catalog.hospitality.frontDesk.allRooms') }} ({{ board()!.rooms.length }})
              </button>
              @for (item of board()!.statusSummary; track item.status) {
                <button
                  type="button"
                  class="summary-chip"
                  [class]="statusClass(item.status)"
                  [class.active]="statusFilter() === item.status"
                  (click)="setStatusFilter(item.status)">
                  {{ statusLabel(item.status) }} ({{ item.count }})
                </button>
              }
            </div>

            @for (floor of floors(); track floor.key) {
              <section class="floor-section">
                <h3>{{ floor.label }}</h3>
                <div class="room-grid">
                  @for (room of floor.rooms; track room.roomUnitId) {
                    <article class="room-card" [class]="statusClass(room.status)">
                      <header>
                        <strong>{{ room.roomNumber }}</strong>
                        <span class="status-badge">{{ statusLabel(room.status) }}</span>
                      </header>
                      <p class="room-type">{{ room.roomTypeCode }} · {{ room.roomTypeName }}</p>

                      @if (room.occupancy) {
                        <div class="occupancy">
                          <span>{{ room.occupancy.customerReference }}</span>
                          <small>{{ room.occupancy.reservationReference }}</small>
                          <small>{{ t('catalog.hospitality.frontDesk.checkOut') }}: {{ room.occupancy.reservationCheckOutUtc | date:'shortDate' }}</small>
                          @if (room.occupancy.folioBalanceDue != null) {
                            <strong>{{ t('catalog.hospitality.folioBalance') }}: {{ room.occupancy.folioBalanceDue | number:'1.2-2' }}</strong>
                          }
                          <a [routerLink]="['/hospitality/reservations', room.occupancy.reservationId]">
                            {{ t('catalog.hospitality.frontDesk.openReservation') }}
                          </a>
                        </div>
                      }

                      @if (room.pendingHousekeeping) {
                        <div class="housekeeping">
                          <small>{{ t('catalog.hospitality.frontDesk.housekeeping') }}: {{ room.pendingHousekeeping.taskType }} · {{ room.pendingHousekeeping.status }}</small>
                        </div>
                      }

                      <footer>
                        <button pButton type="button" size="small" severity="warn" [label]="t('catalog.hospitality.frontDesk.reportMaintenance')" (click)="openMaintenanceDialog(room)"></button>
                        <p-select
                          [options]="statusOptions()"
                          [ngModel]="room.status"
                          (ngModelChange)="updateRoomStatus(room, $event)"
                          optionLabel="label"
                          optionValue="value"
                          appendTo="body" />
                      </footer>
                    </article>
                  }
                </div>
              </section>
            }
          }
        </section>
      }

      <p-dialog
        [header]="t('catalog.hospitality.maintenance.createTitle')"
        [visible]="maintenanceDialogOpen()"
        [modal]="true"
        [style]="{ width: '32rem', maxWidth: '95vw' }"
        (visibleChange)="onMaintenanceDialogVisibleChange($event)">
        @if (pendingMaintenanceRoom(); as room) {
          <p>{{ t('catalog.hospitality.roomNumber') }}: <strong>{{ room.roomNumber }}</strong></p>
          <label class="dialog-field">
            <span>{{ t('catalog.hospitality.maintenance.issue') }}</span>
            <textarea pTextarea rows="3" [(ngModel)]="maintenanceIssue"></textarea>
          </label>
          <label class="checkbox-row">
            <p-checkbox [(ngModel)]="maintenanceCreateWorkOrder" [binary]="true" />
            <span>{{ t('catalog.hospitality.maintenance.createWorkOrder') }}</span>
          </label>
        }
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeMaintenanceDialog()"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="maintenanceSaving() || !maintenanceIssue.trim()" (click)="submitMaintenance()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 16px; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
    .updated-at { font-size: 0.85rem; color: #6b7280; }
    .summary-row { display: flex; flex-wrap: wrap; gap: 8px; }
    .summary-chip {
      border: 1px solid #d1d5db;
      background: #fff;
      border-radius: 999px;
      padding: 6px 12px;
      font-size: 0.85rem;
      cursor: pointer;
    }
    .summary-chip.active { outline: 2px solid #2563eb; }
    .summary-chip.status-available, .room-card.status-available { --accent: #16a34a; }
    .summary-chip.status-reserved, .room-card.status-reserved { --accent: #7c3aed; }
    .summary-chip.status-occupied, .room-card.status-occupied { --accent: #2563eb; }
    .summary-chip.status-dirty, .room-card.status-dirty { --accent: #ea580c; }
    .summary-chip.status-cleaning, .room-card.status-cleaning { --accent: #ca8a04; }
    .summary-chip.status-inspected, .room-card.status-inspected { --accent: #0891b2; }
    .summary-chip.status-outofservice, .room-card.status-outofservice,
    .summary-chip.status-maintenance, .room-card.status-maintenance,
    .summary-chip.status-blocked, .room-card.status-blocked { --accent: #6b7280; }
    .floor-section h3 { margin: 0 0 10px; font-size: 1rem; color: #374151; }
    .room-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 12px;
    }
    .room-card {
      border: 1px solid #e5e7eb;
      border-top: 4px solid var(--accent, #9ca3af);
      border-radius: 8px;
      padding: 12px;
      display: grid;
      gap: 8px;
      background: #fff;
    }
    .room-card header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .status-badge {
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--accent, #374151);
    }
    .room-type { margin: 0; font-size: 0.85rem; color: #6b7280; }
    .occupancy, .housekeeping { display: grid; gap: 4px; font-size: 0.85rem; }
    .occupancy a { color: #1d4ed8; text-decoration: none; font-weight: 600; }
    .room-card footer { margin-top: 4px; display: grid; gap: 8px; }
    .dialog-field { display: grid; gap: 4px; font-size: 13px; margin-top: 8px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
  `]
})
export class HospitalityFrontDeskComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  readonly loading = signal(true);
  readonly board = signal<HospitalityFrontDeskBoard | null>(null);
  readonly statusFilter = signal('');
  readonly properties = signal<HospitalityProperty[]>([]);
  readonly maintenanceDialogOpen = signal(false);
  readonly maintenanceSaving = signal(false);
  readonly pendingMaintenanceRoom = signal<HospitalityFrontDeskRoom | null>(null);

  selectedPropertyId = '';
  maintenanceIssue = '';
  maintenanceCreateWorkOrder = true;

  readonly propertyOptions = computed(() =>
    this.properties().map(p => ({ label: `${p.propertyCode} — ${p.name}`, value: p.id }))
  );

  readonly filteredRooms = computed(() => {
    const data = this.board();
    if (!data) return [] as HospitalityFrontDeskRoom[];
    const filterValue = this.statusFilter();
    return filterValue
      ? data.rooms.filter(room => room.status === filterValue)
      : data.rooms;
  });

  readonly floors = computed(() => {
    const grouped = new Map<string, HospitalityFrontDeskRoom[]>();
    for (const room of this.filteredRooms()) {
      const key = room.floor?.trim() || '__none__';
      const bucket = grouped.get(key) ?? [];
      bucket.push(room);
      grouped.set(key, bucket);
    }

    return [...grouped.entries()]
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
      .map(([key, rooms]) => ({
        key,
        label: key === '__none__'
          ? this.t('catalog.hospitality.frontDesk.unassignedFloor')
          : `${this.t('catalog.hospitality.floor')} ${key}`,
        rooms: rooms.sort((a, b) => a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true }))
      }));
  });

  readonly statusOptions = computed(() =>
    ROOM_STATUSES.map(status => ({
      label: this.t(`catalog.hospitality.roomStatus.${status}`),
      value: status
    }))
  );

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.hospitalityFrontDeskChanged))
      .subscribe(event => this.handleFrontDeskEvent(event.payload));

    this.service.listHospitalityProperties().subscribe({
      next: rows => {
        this.properties.set(rows);
        if (rows.length === 1) {
          this.selectedPropertyId = rows[0].id;
          void this.onPropertyChange();
        } else {
          this.loading.set(false);
        }
      },
      error: () => {
        this.properties.set([]);
        this.loading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  async onPropertyChange(): Promise<void> {
    this.statusFilter.set('');
    this.loadBoard();
    void this.subscribeToTopic();
  }

  loadBoard(showLoading = true): void {
    if (!this.selectedPropertyId) {
      this.board.set(null);
      if (showLoading) this.loading.set(false);
      return;
    }

    if (showLoading) this.loading.set(true);
    this.service.getHospitalityFrontDeskBoard(this.selectedPropertyId).subscribe({
      next: board => {
        this.board.set(board);
        this.loading.set(false);
      },
      error: () => {
        this.board.set(null);
        this.loading.set(false);
      }
    });
  }

  setStatusFilter(status: string): void {
    this.statusFilter.set(status);
  }

  updateRoomStatus(room: HospitalityFrontDeskRoom, status: string): void {
    if (!status || status === room.status) return;

    this.service.updateHospitalityRoomStatus(room.roomUnitId, status).subscribe({
      next: () => this.loadBoard(false),
      error: () => undefined
    });
  }

  statusClass(status: string): string {
    return `status-${status.replace(/\s+/g, '').toLowerCase()}`;
  }

  statusLabel(status: string): string {
    return this.t(`catalog.hospitality.roomStatus.${status}`);
  }

  openMaintenanceDialog(room: HospitalityFrontDeskRoom): void {
    this.pendingMaintenanceRoom.set(room);
    this.maintenanceIssue = '';
    this.maintenanceCreateWorkOrder = true;
    this.maintenanceDialogOpen.set(true);
  }

  onMaintenanceDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeMaintenanceDialog();
  }

  closeMaintenanceDialog(): void {
    this.maintenanceDialogOpen.set(false);
    this.pendingMaintenanceRoom.set(null);
    this.maintenanceIssue = '';
  }

  submitMaintenance(): void {
    const room = this.pendingMaintenanceRoom();
    if (!room || !this.maintenanceIssue.trim()) return;

    this.maintenanceSaving.set(true);
    this.service.createRoomMaintenanceRequest({
      roomUnitId: room.roomUnitId,
      issueDescription: this.maintenanceIssue.trim(),
      createWorkOrder: this.maintenanceCreateWorkOrder
    }).subscribe({
      next: () => {
        this.maintenanceSaving.set(false);
        this.closeMaintenanceDialog();
        this.loadBoard(false);
      },
      error: () => this.maintenanceSaving.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }

  private handleFrontDeskEvent(payload: Record<string, unknown>): void {
    const propertyId = payload['propertyId'];
    if (typeof propertyId !== 'string' || propertyId !== this.selectedPropertyId)
      return;

    this.loadBoard(false);
  }

  private async subscribeToTopic(): Promise<void> {
    await this.unsubscribeFromTopic();

    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId || !this.selectedPropertyId)
      return;

    this.subscribedTopic = hospitalityFrontDeskTopic(tenantId, this.selectedPropertyId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic)
      return;

    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }
}
